import { armMatchCutDuplicateRealignment, VideoData } from '@lumen5/luminary';

import { getTemplateBundle, isTemplateBundleLoading } from 'js/src/data/templates';
import { getVideoData } from 'js/src/data/video-data';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';
import { updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation } from 'js/src/luminary/voicedriven-scenegroup-utils';
import ActivityProfiler from 'js/src/profiling/ActivityProfiler';
import { METRIC_INITIAL_VIDEO_LOAD } from 'js/src/profiling/metrics';

/**
 * When the Video Creator loads, we get video_data and we supply that to luminary.
 * Luminary loads the video.
 */
export const initializeLuminaryOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState, call }) => {
        // track how long it takes for all the scene design previews to be updated
        // when first loading the video
        const activityId = uuid();
        ActivityProfiler.start(activityId);
        const videoData = getVideoData(getState());
        if (!videoData) {
            throw new Error('Video data is missing when initializing the VideoCreator');
        }
        if (luminary.destroyed) {
            return;
        }
        luminary.load(videoData);
        // Arm before luminary's own rehydration creates the stand-ins, so a drag during the initial
        // render can't strand one. Realignment no-ops until stand-ins exist.
        const hasPersistedMatchCut = luminary.scenes.some(
            (scene) => scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE,
        );
        if (hasPersistedMatchCut) {
            armMatchCutDuplicateRealignment(luminary);
        }
        if (!isTemplateBundleLoading(getState(), luminary.template.id)) {
            luminary.setTemplate(getTemplateBundle(getState(), luminary.template.id)!);
        }

        // Make sure that all voicedriven scenegroups are correctly configured
        luminary.sceneGroups.forEach((sceneGroup) => {
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                call(updateSceneGroupDurationToEqualVoiceDrivenAssetDurationOperation, {
                    sceneGroup,
                });
            }
        });

        // mark the tracking as complete when all scene images have finished rendering
        await luminary.onAllSlideImagesRendered();

        const metricLabels = METRIC_INITIAL_VIDEO_LOAD.getLabels(luminary.scenes.length);
        ActivityProfiler.end(activityId, METRIC_INITIAL_VIDEO_LOAD.name, metricLabels);
    },
);
